import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';
import { useParams, useHistory } from 'react-router-dom';
import groupBy from 'lodash/groupBy';
import moment from 'moment';

import OrderItemMeal from '../../components/OrderItemMeal';
// import Addons from '../../components/V2/Addons';
import BillingAddress from '../../components/V2/BillingAddress';
import { useCutoffTime } from './UserManagementPage/hooks';
import DeliveryDay from './UserManagementPage/DeliveryDay';
import { ReactComponent as InfoIconOutline } from 'core/assets/images/V2/icons/InfoIconOutline.svg';
import { ReactComponent as CreditedIcon } from 'core/assets/images/V2/icons/CreditedIcon.svg';
import Money from 'core/api/Money';
import { useMyOrderPage } from 'onramp/hooks/useMyOrderPage';
import WeekContainer from 'onramp/ui/components/WeekContainer';
import NavigationHeader from 'onramp/ui/components/NavigationHeader';
import { Box, Button, Icon, Flex, Hr, Text, Line } from 'core/ui/atoms';
import Loading from 'core/ui/components/Loading';
import { isLoading } from 'core/api/loadingHelpers';
import {
  GET_PRODUCT_COMPONENTS_BY_DELIVERY_DAY,
  GET_SWAPPED_PRODUCT_COMPONENTS_BY_DELIVERY_DAY,
  LIST_UNLOCKED_AVAILABLE_DELIVERY_DAYS,
} from 'core/api/apollo/queries';
import { orderManagementPath, myOrderPath } from 'onramp/api/navigation';
import SplitPageSidebar from 'core/ui/components/SplitPageSidebar';
import ResponsiveView from 'core/ui/components/ResponsiveView';
import OrderItem from 'onramp/ui/components/OrderItem';

const EditWeekMenuPage = () => {
  const { deliveryDayId } = useParams();
  const history = useHistory();
  const variables = { deliveryDayId: deliveryDayId };
  const [showCreditPopover, setShowCreditPopover] = useState(false);

  const {
    userSwapBillingSummary,
    // refetchBillingSummary, // Required if Addons component is used
  } = useMyOrderPage();

  const {
    cutoffDateTime,
    formattedDateTime,
  } = useCutoffTime(deliveryDayId);

  const { loading, error, data: {
    getProductComponentsByDeliveryDay = [] } = {},
  } = useQuery(GET_PRODUCT_COMPONENTS_BY_DELIVERY_DAY, {
    variables,
  });

  const {
    data: { getSwappedProductComponentsByDeliveryDay = [] } = {},
  } = useQuery(GET_SWAPPED_PRODUCT_COMPONENTS_BY_DELIVERY_DAY, {
    variables,
    skip: loading,
  });

  const {
    loading: deliveryDaysLoading,
    error: deliveryDaysError,
    data: {
      listUnlockedAvailableDeliveryDays = [],
    } = {},
  } = useQuery(LIST_UNLOCKED_AVAILABLE_DELIVERY_DAYS);

  const getWeekDate = () => {
    if (deliveryDaysLoading || deliveryDaysError) return null;
    const currentDeliveryDay = listUnlockedAvailableDeliveryDays.find((item) => item.id === deliveryDayId);
    return currentDeliveryDay?.date;
  };

  const onMyOrderNavigate = () => {
    history.push(myOrderPath());
  };

  const backNavigation = () => history.replace(orderManagementPath());

  const RenderWeekDate = () => {
    const formattedDate = getWeekDate();
    return (
      <WeekContainer>Week of
        <WeekDataText>{moment(formattedDate).format('MMMM Do')}</WeekDataText>
      </WeekContainer>
    );
  };

  if (isLoading(loading, getProductComponentsByDeliveryDay, error)) {
    return <Loading />;
  }

  const { creditsTotalAmount, finalAmount } = userSwapBillingSummary;

  const groupProductByDay = groupBy(getProductComponentsByDeliveryDay, 'day');

  const dayList = Object.keys(groupProductByDay).filter((data) => data !== 'null');

  const amount = creditsTotalAmount?.amount < 0 ? creditsTotalAmount : finalAmount;

  const ordersDescriptionText = () => {
    const now = moment();
    const cutoffDate = moment(cutoffDateTime);
    const TenDaysLater = cutoffDate.add(2, 'days');
    if (cutoffDate.isBefore(now)) {
      return `The cut-off to edit this order has passed. If you need to make changes to your order, email us at help@gomethodology.com by 9:00 AM PT on ${TenDaysLater.format('dddd, MMMM Do')}.`;
    }
    if (cutoffDate.isAfter(now)) {
      return `Edit this week's order until ${formattedDateTime.time} PST on ${formattedDateTime.date}`;
    }
  };

  const renderOrderItem = (orderItems) => {
    return orderItems.map((item, index) => {
      const swappedItem = getSwappedProductComponentsByDeliveryDay.find((swapped) => swapped.originalComponentId === item.id);
      if (swappedItem) {
        const swappedItemWithFlag = { ...swappedItem, swapped: true, editable: false };
        return <OrderItemMeal item={swappedItemWithFlag} key={index} deliveryDayId={deliveryDayId} />;
      }
      return <OrderItemMeal item={item} key={index} deliveryDayId={deliveryDayId} />;
    });
  };

  const renderDesktopOrderItem = (orderItems) => {
    return orderItems.map((item, index) => {
      const swappedItem = getSwappedProductComponentsByDeliveryDay.find((swapped) => swapped.originalComponentId === item.id);
      if (swappedItem) {
        const swappedItemWithFlag = { ...swappedItem, swapped: true, editable: false };
        return (
          <OrderItem
            isEditMeal={true}
            item={swappedItemWithFlag}
            key={index}
            deliveryDayId={deliveryDayId}
          />
        );
      }
      return (
        <OrderItem
          isEditMeal={true}
          item={item}
          key={index}
          deliveryDayId={deliveryDayId}
        />
      );
    });
  };

  const RenderMobileItem = () => {
    return dayList.map((dayItem, index) => (
      <Box key={index}>
        <Flex direction='row' alignItems='center' justifyContent='center' gap='16px'>
          <Line />
          <DayText>{`Day ${dayItem}`}</DayText>
          <Line />
        </Flex>
        <ScrollableContent>
          {renderOrderItem(groupProductByDay[dayItem])}
        </ScrollableContent>
      </Box>
    ));
  };

  const getIcon = () => amount?.amount < 0 && <CreditIcon />;

  const amountFormat = { __typename: 'Money', amount: Math.abs(amount?.amount || 0) };

  const LeftSideBarView = () => (
    <LeftContainer>
      <RenderConfirmButton />
      <AddressTitle>This Week's Delivery Address</AddressTitle>
      <BillingAddress />
      <DeliveryDay />
    </LeftContainer>
  );

  const scrollRefs = Array(dayList.length).fill().map(() => React.createRef());

  const RenderDesktopItem = () => {
    return dayList.map((dayItem, index) => {
      const scrollRef = scrollRefs[index];

      const scrollLeft = () => {
        if (scrollRef.current) {
          scrollRef.current.scrollBy({
            left: -300, // Adjust scroll distance as needed
            behavior: 'smooth',
          });
        }
      };

      const scrollRight = () => {
        if (scrollRef.current) {
          scrollRef.current.scrollBy({
            left: 300, // Adjust scroll distance as needed
            behavior: 'smooth',
          });
        }
      };

      return (
        <Box key={index}>
          <Flex direction='row' alignItems='center' justifyContent='center' gap='16px'>
            <Line />
            <DayText>{`Day ${dayItem}`}</DayText>
            <Line />
          </Flex>
          <ScrollContainer>
            <CaretButton btn-unstyled onClick={scrollLeft} position="left">
              <Caret icon='arrow_l' />
            </CaretButton>
            <ScrollableContent ref={scrollRef}>
              {renderDesktopOrderItem(groupProductByDay[dayItem])}
            </ScrollableContent>
            <CaretButton btn-unstyled onClick={scrollRight} position="right">
              <Caret icon='arrow_r' />
            </CaretButton>
          </ScrollContainer>
        </Box>
      );
    });
  };

  const RenderConfirmButton = () => (
    <ConfirmMealButtonContainer direction='column' alignItems='center'>
      <ConfirmButton onClick={onMyOrderNavigate}>
        CONFIRM & CHECK OUT - {amount?.amount < 0 && getIcon()} {Money(amountFormat).toFormat('$0,0')}
      </ConfirmButton>
      {amount?.amount < 0 &&
        <InfoContainer>
          <Flex direction='row' justifyContent='center' alignItems='center' gap='5px'>
            <Text>credit amount</Text>
            <ClickableInfoIcon onClick={() => setShowCreditPopover(!showCreditPopover)} />
          </Flex>
          {showCreditPopover && (
            <PopoverContainer>
              <PopoverContent>
                <PopoverText>
                  Your credits will be used as a discount on your next order.
                  No expiry. No pressure. Just tasty savings.
                </PopoverText>
                <CloseButton onClick={() => setShowCreditPopover(false)}>✕</CloseButton>
              </PopoverContent>
            </PopoverContainer>
          )}
        </InfoContainer>
      }
    </ConfirmMealButtonContainer>
  );

  return (
    <Box>
      <ResponsiveView desktop={true}>
        <NavigationHeader title="Edit Your Order" backNavigate={backNavigation} />
        <Hr />
        <DateContainer>
          <WeekText>{ordersDescriptionText()}</WeekText>
        </DateContainer>
      </ResponsiveView>
      <SplitPageSidebar sidebarView={<LeftSideBarView />}>
        <MainContainer>
          <ResponsiveView desktop={false}>
            <NavigationHeader title="Edit Your Order" backNavigate={backNavigation} />
            <RenderWeekDate />
            <DateContainer>
              <WeekText>{ordersDescriptionText()}</WeekText>
            </DateContainer>
          </ResponsiveView>
          <ResponsiveView desktop={true}>
            <RenderWeekDate />
          </ResponsiveView>
          <ResponsiveView desktop={false}>
            <RenderMobileItem />
          </ResponsiveView>
          <ResponsiveView desktop={true}>
            <RenderDesktopItem />
          </ResponsiveView>
          {/* <Hr /> */}
          {/* <Addons deliveryDayId={deliveryDayId} refetchBillingSummary={refetchBillingSummary} /> */}
          <AddressSection>
            <Hr />
            <AddressTitle>This Week Delivery Address</AddressTitle>
            <BillingAddress />
          </AddressSection>
          <ResponsiveView desktop={false}>
            <DeliveryDay />
            <RenderConfirmButton />
          </ResponsiveView>
        </MainContainer>
      </SplitPageSidebar>
    </Box>
  );
};

const ScrollContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
`;

const AddressSection = styled(Box)`
  display: none;
  ${(p) => p.theme.max('sm')`
    display: block;
    padding-left: 20px;
    padding-right: 20px;
    width: 100%;
  `}
`;

const CaretButton = styled(Button)`
  color: ${({ theme }) => theme.colors.primary};
  ${(p) => p.theme.max('sm')`
    display: none;
  `}
`;

const Container = styled(Box)`
  margin-top: 15px;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 0px;
`;

const MainContainer = styled(Container)`
  margin-top: 0px;
  ${(p) => p.theme.max('sm')`
    margin-top: 15px;
  `}
`;

const LeftContainer = styled(Box)`
  width: 90%;
`;

const Caret = styled(Icon)`
  display: inline-block;
  transition: transform 0.25s ease-in-out;
  transform-style: preserve-3d;
  transform: rotateX(${(p) => p.$open ? '180deg' : '0deg'});
`;

const WeekDataText = styled(Text)`
  color: ${(p) => p.theme.colors.creamWhite};
  font-family: ${({ theme }) => theme.typography.familyLarken};
  font-size: 18px;
  font-style: italic;
  font-weight: 800;
  line-height: 32px;
  letter-spacing: 0.54px;
  margin: 0px;
`;

const DateContainer = styled(Box)`
  margin-top: 20px;
  margin-bottom: 20px;
`;


const InfoContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ClickableInfoIcon = styled(InfoIconOutline)`
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const PopoverContainer = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 10px;
  z-index: 1000;
`;

const PopoverContent = styled.div`
  background: ${(p) => p.theme.colors.white};
  border: 1px solid ${(p) => p.theme.colors.grays.light};
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  width: 280px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 8px solid transparent;
    border-top-color: ${(p) => p.theme.colors.white};
  }

  &::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 9px solid transparent;
    border-top-color: ${(p) => p.theme.colors.grays.light};
    z-index: -1;
  }
`;

const PopoverText = styled(Text)`
  font-size: 14px;
  line-height: 1.4;
  color: ${(p) => p.theme.colors.black};
  margin: 0;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: ${(p) => p.theme.colors.grays.medium};
  padding: 2px;
  line-height: 1;

  &:hover {
    color: ${(p) => p.theme.colors.black};
  }
`;

const CreditIcon = styled(CreditedIcon)`
  margin-bottom: 5px;
`;

const ConfirmButton = styled(Button)`
  padding: 12px 10px;
  border-radius: 4px;
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 0px;
  background: ${(p) => p.theme.colors.green.light};
  ${(p) => p.theme.max('sm')`
    margin-bottom: 0;
    width: 95%;
    margin-top: 15px;
  `}
`;

const ConfirmMealButtonContainer = styled(Flex)`
  justify-content: center;
  width: 100%;
  padding: 0px;
  height: auto;
  ${(p) => p.theme.max('sm')`
    background-color: ${(p) => p.theme.colors.creamWhite};
    position: fixed;
    padding: 20px;
    height: 110px;
    bottom: 0;
    left: 0;
  `}
`;

const AddressTitle = styled(Text)`
  text-align: start;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
  margin-top: 44px;
  color: ${(p) => p.theme.colors.primaryV2};
  font-family: ${({ theme }) => theme.typography.familyLarken};
  ${(p) => p.theme.max('sm')`
    text-align: center;
    margin-top: 0px;
    font-size: 22px;
  `}
`;
const WeekText = styled(Text)`
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  color: ${(p) => p.theme.colors.primaryV2};
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  padding-left: 16px;
  padding-right: 16px;
  margin: 0px;
`;

const DayText = styled(Text)`
  text-align: center;
  color: ${(p) => p.theme.colors.primaryV2};
  text-align: center;
  font-family: ${({ theme }) => theme.typography.familyLarken};
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
  margin-bottom: 20px;
  margin-top: 20px;
`;

const ScrollableContent = styled.div`
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  width: 100%;
  gap: 24px;
  padding-bottom: 10px; /* Space for scrollbar */
  scrollbar-width: none;
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  overflow-y: auto;
  column-gap: 25px;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    height: 6px;
  }

  /* Track */
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  /* Handle on hover */
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }

  /* For Firefox */
  scrollbar-color: #888 #f1f1f1;
  ${(p) => p.theme.max('md')`
    flex-direction: column;
  `}
`;

export default EditWeekMenuPage;
